'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import {
  Award,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  Gift,
  HandCoins,
  Heart,
  HelpCircle,
  Info,
  LayoutDashboard,
  LogOut,
  Mail,
  Menu,
  Package,
  Search,
  Settings as SettingsIcon,
  ShieldCheck,
  User as UserIcon,
  Users,
  X,
} from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { buildLocalePath, type Locale } from '@/i18n/config';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu/dropdown-menu';
import { Input } from '@/components/ui/input/input';
import { useRegional } from '@/components/providers/regional-provider';
import { useSearchStore } from '@/stores/search-store';
import { Flag } from '@/components/ui/flag/flag';
import { useAuth } from '@/hooks/use-auth';
import { useLogout } from '@/hooks/use-logout';
import type { CurrencyOption, PublicUserInfo } from '@impact/types';

interface UwrCurrencyOption extends CurrencyOption {
  countryCode: string;
}

const UWR_CURRENCIES: UwrCurrencyOption[] = [
  { code: 'AUD', symbol: 'A$', label: 'Australian Dollar', countryCode: 'AU' },
  { code: 'NZD', symbol: 'NZ$', label: 'New Zealand Dollar', countryCode: 'NZ' },
];

const DEFAULT_CURRENCY = 'AUD';

interface NavChild {
  label: string;
  description?: string;
  href: string;
  icon?: React.ReactNode;
}

interface NavItem {
  label: string;
  href?: string;
  children?: NavChild[];
}

export interface PublicHeaderProps {
  tenantName?: string;
  logo?: React.ReactNode;
  loginHref?: string;
  variant?: 'default' | 'transparent';
  solidBackground?: boolean;
  /** Available currencies (unused — locked to AUD/NZD for UWR) */
  currencies?: CurrencyOption[];
  user?: unknown;
  onLogout?: () => void;
  /** When false, header participates in layout instead of overlaying page content */
  overlay?: boolean;
}

export function PublicHeader({
  loginHref = '/login',
  variant = 'transparent',
  solidBackground = false,
  overlay = true,
}: PublicHeaderProps) {
  const tNav = useTranslations('nav');
  const tCommon = useTranslations('common');
  const tUwrFooter = useTranslations('uwrFooter');
  const tUwrAbout = useTranslations('uwrAboutMenu');
  const tMy = useTranslations('my');
  const locale = useLocale() as Locale;
  const { currency: regionalCurrency, setCurrency } = useRegional();

  // ───────── Auth ─────────
  const {
    user: authUser,
    userType: authUserType,
    authContext,
  } = useAuth({ hydrateFromQuery: true });
  const { logout, isLoggingOut } = useLogout();
  const user: PublicUserInfo | undefined =
    authUser && authUserType
      ? {
          name: authUser.name,
          email: authUser.email,
          userType: authUserType,
          avatarUrl: authContext?.entrant?.profileImageUrl ?? undefined,
        }
      : undefined;
  const entrantUsername = authContext?.entrant?.username ?? undefined;

  // ───────── Admin workspace access (mirrors DGR PublicHeader) ─────────
  const hasAdminDashboardAccess =
    authContext?.role?.slug === 'owner' ||
    authContext?.role?.slug === 'admin' ||
    authContext?.role?.slug === 'operations_admin';
  const hasEventAdminAccess = Boolean(authContext?.eventAdmin);
  const workspaceLinks = [
    ...(hasAdminDashboardAccess
      ? [{ href: '/dashboard', icon: LayoutDashboard, label: tNav('adminDashboard') }]
      : []),
    ...(hasEventAdminAccess
      ? [{ href: '/event-admin', icon: ShieldCheck, label: tNav('eventAdmin') }]
      : []),
  ];

  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSubmenu, setMobileSubmenu] = useState<string | null>(null);
  const [accountOpen, setAccountOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // ───────── Search ─────────
  const router = useRouter();
  const { startSearchTransition } = useSearchStore();
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchModalOpen) {
      const t = setTimeout(() => searchInputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [searchModalOpen]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = searchQuery.trim();
    if (!trimmed) return;
    startSearchTransition(trimmed);
    setSearchModalOpen(false);
    setSearchQuery('');
    router.push(`${buildLocalePath(locale, '/search')}?q=${encodeURIComponent(trimmed)}`);
  };

  // Lock currency to AUD/NZD; default to AUD on first render.
  useEffect(() => {
    if (regionalCurrency !== 'AUD' && regionalCurrency !== 'NZD') {
      setCurrency(DEFAULT_CURRENCY);
    }
  }, [regionalCurrency, setCurrency]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Prevent body scroll when any mobile drawer is open
  useEffect(() => {
    if (mobileOpen || accountOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen, accountOpen]);

  const closeMobile = () => {
    setMobileOpen(false);
    setMobileSubmenu(null);
  };
  const closeAccount = () => setAccountOpen(false);

  const isTransparent = variant === 'transparent';
  const activeCurrency =
    UWR_CURRENCIES.find((c) => c.code === regionalCurrency) ?? UWR_CURRENCIES[0];

  const aboutChildren: NavChild[] = [
    {
      label: tUwrFooter('aboutUwr'),
      description: tUwrAbout('aboutUwrDesc'),
      href: '/about',
      icon: <Info className="w-5 h-5" />,
    },
    {
      label: tUwrFooter('salvationArmy'),
      description: tUwrAbout('salvationArmyDesc'),
      href: '/about/the-salvation-army',
      icon: <Heart className="w-5 h-5" />,
    },
    {
      label: tUwrFooter('ourPartners'),
      description: tUwrAbout('ourPartnersDesc'),
      href: '/about/partners',
      icon: <HandCoins className="w-5 h-5" />,
    },
    {
      label: tUwrFooter('faqs'),
      description: tUwrAbout('faqsDesc'),
      href: '/about/faq',
      icon: <HelpCircle className="w-5 h-5" />,
    },
    {
      label: tUwrFooter('prizes'),
      description: tUwrAbout('prizesDesc'),
      href: '/about/prizes',
      icon: <Award className="w-5 h-5" />,
    },
    {
      label: tUwrFooter('safetyInformation'),
      description: tUwrAbout('safetyInformationDesc'),
      href: '/about/safety-information',
      icon: <ShieldCheck className="w-5 h-5" />,
    },
    {
      label: tUwrFooter('partnerTeams'),
      description: tUwrAbout('partnerTeamsDesc'),
      href: '/partner-teams',
      icon: <Users className="w-5 h-5" />,
    },
    {
      label: tUwrFooter('contactUs'),
      description: tUwrAbout('contactUsDesc'),
      href: '/contact',
      icon: <Mail className="w-5 h-5" />,
    },
  ];

  const navItems: NavItem[] = [
    { label: tNav('about'), children: aboutChildren },
    { label: tNav('events'), href: '/events' },
    { label: tNav('leaderboard'), href: '/leaderboard' },
  ];

  return (
    <>
      <header
        className={cn(
          overlay ? 'absolute top-0 left-0 right-0 z-50 w-full' : 'relative z-50 w-full',
          'border-t-2 border-uwr-orange transition-colors duration-300',
          // Top-to-bottom fade so the header bleeds into the hero instead of
          // sitting as a hard dark band. Stays solid when scrolled or non-transparent.
          isTransparent &&
            !solidBackground &&
            !scrolled &&
            'bg-gradient-to-b from-stone-950 via-stone-950/70 to-transparent',
          isTransparent && (solidBackground || scrolled) && 'bg-stone-950',
          !isTransparent && 'bg-stone-950',
        )}
      >
        <div className="container flex h-20 items-center justify-between gap-4">
          {/* Logo */}
          <Link
            prefetch={false}
            href={buildLocalePath(locale, '/')}
            className="flex items-center gap-3 shrink-0"
          >
            <Image
              src="/images/uwr/UWR_Logo_General.png"
              alt={tCommon('brandAlt')}
              width={140}
              height={60}
              priority
              className="h-10 lg:h-12 w-auto"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-7 text-sm font-extrabold uppercase tracking-wide text-white/90">
            {navItems.map((item) => {
              if (item.children) {
                return (
                  <DropdownMenu key={item.label}>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="flex items-center gap-1 uppercase tracking-wide hover:text-uwr-orange transition-colors"
                      >
                        {item.label}
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="start"
                      sideOffset={12}
                      className="w-[340px] p-2 border-0 shadow-xl rounded-xl"
                    >
                      {item.children.map((child) => (
                        <Link
                          prefetch={false}
                          key={child.href}
                          href={buildLocalePath(locale, child.href)}
                          className="group flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-stone-100 transition-colors"
                        >
                          {child.icon && (
                            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center text-stone-500 group-hover:bg-uwr-orange/10 group-hover:text-uwr-orange transition-colors">
                              {child.icon}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-sm text-stone-900 normal-case tracking-normal">
                              {child.label}
                            </div>
                            {child.description && (
                              <div className="text-xs text-stone-500 mt-0.5 line-clamp-2 normal-case tracking-normal font-normal">
                                {child.description}
                              </div>
                            )}
                          </div>
                        </Link>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              }
              return (
                <Link
                  key={item.href}
                  href={buildLocalePath(locale, item.href as string)}
                  prefetch={false}
                  className="hover:text-uwr-orange transition-colors"
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Right actions */}
          <div className="hidden md:flex items-center gap-3">
            {/* Search */}
            <Dialog open={searchModalOpen} onOpenChange={setSearchModalOpen}>
              <DialogTrigger asChild>
                <button
                  type="button"
                  aria-label={tNav('search')}
                  className="text-white/85 hover:text-white hover:bg-white/10 p-2 rounded-md transition-colors"
                >
                  <Search className="w-4 h-4" />
                </button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl top-[15%] translate-y-0">
                <DialogHeader>
                  <DialogTitle className="text-xl">{tNav('search')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSearch} className="mt-2">
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      ref={searchInputRef}
                      type="text"
                      placeholder={tNav('searchPlaceholder')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="h-14 pl-12 pr-4 text-lg bg-muted/50 border-border focus-visible:ring-uwr-orange"
                    />
                  </div>
                  <div className="flex items-center justify-end mt-4">
                    <Button
                      type="submit"
                      disabled={!searchQuery.trim()}
                      className="bg-uwr-orange hover:bg-uwr-orange-hover text-white font-semibold"
                    >
                      {tNav('search')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            {/* Currency picker (AUD / NZD only) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-extrabold text-white/85 hover:text-white hover:bg-white/10 transition-colors"
                  aria-label="Currency"
                >
                  <Flag code={activeCurrency.countryCode} size="sm" className="rounded-sm" />
                  <span>{activeCurrency.code}</span>
                  <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={12}
                className="w-40 p-2 border-0 shadow-xl rounded-xl"
              >
                {UWR_CURRENCIES.map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => setCurrency(c.code)}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-stone-100 transition-colors text-left',
                      regionalCurrency === c.code && 'bg-stone-100',
                    )}
                  >
                    <Flag code={c.countryCode} size="sm" className="w-6 shrink-0 rounded-sm" />
                    <span className="w-9 shrink-0 font-mono text-xs text-stone-500">
                      {c.symbol}
                    </span>
                    <span className="flex-1 text-sm font-semibold text-stone-900">{c.code}</span>
                  </button>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {user ? (
              // Logged-in: user avatar dropdown
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={cn(
                      'flex items-center gap-2 rounded-full pr-3 transition-colors border-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-uwr-orange/50',
                      'border-uwr-orange hover:border-uwr-orange-light data-[state=open]:border-uwr-orange',
                      'bg-black/50 backdrop-blur-sm hover:bg-black/60',
                    )}
                    aria-label={user.name}
                  >
                    <span className="flex-shrink-0 w-9 h-9 rounded-full overflow-hidden">
                      {user.avatarUrl ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={user.avatarUrl}
                          alt={user.name}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        <span className="w-full h-full flex items-center justify-center text-sm font-extrabold bg-white text-stone-950">
                          {user.name.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </span>
                    <span className="text-sm font-extrabold max-w-[100px] truncate text-white">
                      {user.name.split(' ')[0]}
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  sideOffset={12}
                  className="w-60 p-1 border-0 shadow-xl rounded-xl"
                >
                  <div className="px-3 py-2 border-b border-stone-100">
                    <p className="font-bold text-sm truncate text-stone-900">{user.name}</p>
                    {user.email && <p className="text-xs text-stone-500 truncate">{user.email}</p>}
                  </div>
                  {workspaceLinks.length > 0 && (
                    <>
                      {workspaceLinks.map((workspace) => {
                        const Icon = workspace.icon;
                        return (
                          <DropdownMenuItem
                            key={workspace.href}
                            asChild
                            className="cursor-pointer text-uwr-orange focus:text-uwr-orange font-semibold"
                          >
                            <Link prefetch={false} href={buildLocalePath(locale, workspace.href)}>
                              <Icon className="h-4 w-4 mr-2" />
                              {workspace.label}
                            </Link>
                          </DropdownMenuItem>
                        );
                      })}
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link prefetch={false} href={buildLocalePath(locale, '/my/dashboard')}>
                      <LayoutDashboard className="h-4 w-4 mr-2" />
                      {tNav('myDashboard')}
                    </Link>
                  </DropdownMenuItem>
                  {entrantUsername && (
                    <DropdownMenuItem asChild className="cursor-pointer">
                      <Link
                        prefetch={false}
                        href={buildLocalePath(locale, `/fundraiser/${entrantUsername}`)}
                      >
                        <UserIcon className="h-4 w-4 mr-2" />
                        {tMy('viewPublicProfile')}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link prefetch={false} href={buildLocalePath(locale, '/my/events')}>
                      <CalendarDays className="h-4 w-4 mr-2" />
                      {tNav('myEvents')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link prefetch={false} href={buildLocalePath(locale, '/my/donations')}>
                      <Heart className="h-4 w-4 mr-2" />
                      {tNav('myDonations')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link prefetch={false} href={buildLocalePath(locale, '/my/downloads')}>
                      <Download className="h-4 w-4 mr-2" />
                      {tMy('navDownloads')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link prefetch={false} href={buildLocalePath(locale, '/my/offers')}>
                      <Gift className="h-4 w-4 mr-2" />
                      {tMy('navOffers')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link prefetch={false} href={buildLocalePath(locale, '/my/team/dashboard')}>
                      <Users className="h-4 w-4 mr-2" />
                      {tMy('navTeam')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link prefetch={false} href={buildLocalePath(locale, '/my/settings')}>
                      <SettingsIcon className="h-4 w-4 mr-2" />
                      {tMy('navSettings')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => void logout()}
                    disabled={isLoggingOut}
                    className="cursor-pointer"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    {isLoggingOut ? tNav('signingOut') : tNav('logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button
                  variant="ghost"
                  asChild
                  className="border border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white text-xs uppercase tracking-wider font-extrabold h-9 px-4"
                >
                  <Link prefetch={false} href={buildLocalePath(locale, loginHref)}>
                    {tNav('login')}
                  </Link>
                </Button>

                <Button
                  asChild
                  className="bg-stone-900 hover:bg-stone-800 text-white text-xs uppercase tracking-wider font-extrabold h-9 px-4 border border-white/10"
                >
                  <Link prefetch={false} href={buildLocalePath(locale, '/register')}>
                    {tNav('register')}
                  </Link>
                </Button>
              </>
            )}

            <Button
              asChild
              className="bg-uwr-orange hover:bg-uwr-orange-hover text-white text-xs uppercase tracking-wider font-extrabold h-9 px-4"
            >
              <Link prefetch={false} href={buildLocalePath(locale, '/donate')}>
                {tNav('donate')}
              </Link>
            </Button>
          </div>

          {/* Mobile triggers: avatar (account) + hamburger (nav) */}
          <div className="md:hidden flex items-center gap-2 -mr-2">
            {user && (
              <button
                type="button"
                onClick={() => setAccountOpen(true)}
                className={cn(
                  'flex items-center rounded-full border-2 border-uwr-orange overflow-hidden transition-transform',
                  'focus:outline-none focus:ring-2 focus:ring-uwr-orange/50',
                )}
                aria-label={user.name}
              >
                <span className="block w-9 h-9">
                  {user.avatarUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={user.avatarUrl}
                      alt={user.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="w-full h-full flex items-center justify-center text-sm font-extrabold bg-white text-stone-950">
                      {user.name.charAt(0).toUpperCase()}
                    </span>
                  )}
                </span>
              </button>
            )}
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="text-white p-2"
              aria-label={mobileOpen ? tCommon('close') : 'Menu'}
            >
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile overlay drawer */}
      <div
        className={cn(
          'md:hidden fixed inset-0 z-[60] flex flex-col bg-stone-950 transition-all duration-300 ease-out',
          mobileOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none',
        )}
      >
        {/* Fixed header (logo + close) */}
        <div className="flex-shrink-0 h-20 flex items-center justify-between px-6 border-b border-white/10 border-t-2 border-t-uwr-orange">
          <Link
            prefetch={false}
            href={buildLocalePath(locale, '/')}
            onClick={closeMobile}
            className="flex items-center gap-3"
          >
            <Image
              src="/images/uwr/UWR_Logo_General.png"
              alt={tCommon('brandAlt')}
              width={140}
              height={60}
              className="h-10 w-auto"
            />
          </Link>
          <button
            type="button"
            onClick={closeMobile}
            className="text-white p-2 -mr-2"
            aria-label={tCommon('close')}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Sliding panel container */}
        <div className="flex-1 overflow-hidden relative">
          {/* Main panel */}
          <div
            className={cn(
              'absolute inset-0 overflow-y-auto px-6 py-3 transition-transform duration-300 ease-out',
              mobileSubmenu ? '-translate-x-full' : 'translate-x-0',
            )}
          >
            <nav className="flex flex-col">
              {navItems.map((item) =>
                item.children ? (
                  <button
                    key={item.label}
                    type="button"
                    onClick={() => setMobileSubmenu(item.label)}
                    className="w-full flex items-center justify-between py-2.5 text-base font-extrabold uppercase tracking-wide text-white border-b border-white/10 hover:text-uwr-orange transition-colors"
                  >
                    <span>{item.label}</span>
                    <ChevronRight className="w-4 h-4 opacity-60" />
                  </button>
                ) : (
                  <Link
                    key={item.label}
                    href={buildLocalePath(locale, item.href as string)}
                    prefetch={false}
                    onClick={closeMobile}
                    className="py-2.5 text-base font-extrabold uppercase tracking-wide text-white border-b border-white/10 hover:text-uwr-orange transition-colors"
                  >
                    {item.label}
                  </Link>
                ),
              )}
            </nav>

            {/* Primary CTAs — donate is always visible; login/register only
                when anonymous. My-account stuff lives in the avatar drawer now. */}
            <div className="mt-4 space-y-2">
              <Button
                asChild
                className="bg-uwr-orange hover:bg-uwr-orange-hover text-white font-extrabold uppercase tracking-wider w-full h-10 rounded-full text-sm"
              >
                <Link
                  prefetch={false}
                  href={buildLocalePath(locale, '/donate')}
                  onClick={closeMobile}
                >
                  {tNav('donate')}
                </Link>
              </Button>
              {!user && (
                <>
                  <Button
                    asChild
                    className="bg-white hover:bg-white/90 text-stone-950 font-extrabold uppercase tracking-wider w-full h-10 rounded-full text-sm"
                  >
                    <Link
                      prefetch={false}
                      href={buildLocalePath(locale, '/register')}
                      onClick={closeMobile}
                    >
                      {tNav('register')}
                    </Link>
                  </Button>
                  <Button
                    asChild
                    variant="ghost"
                    className="border border-white/30 text-white hover:bg-white/10 hover:text-white font-extrabold uppercase tracking-wider w-full h-10 rounded-full text-sm"
                  >
                    <Link
                      prefetch={false}
                      href={buildLocalePath(locale, loginHref)}
                      onClick={closeMobile}
                    >
                      {tNav('login')}
                    </Link>
                  </Button>
                </>
              )}
            </div>

            {/* Currency switcher */}
            <div className="mt-4">
              <p className="text-[10px] uppercase tracking-widest text-white/55 mb-2">
                {tNav('currency')}
              </p>
              <div className="flex items-center gap-2">
                {UWR_CURRENCIES.map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => setCurrency(c.code)}
                    className={cn(
                      'flex-1 inline-flex items-center justify-center gap-2 py-2 rounded-full text-xs font-extrabold border transition-colors',
                      regionalCurrency === c.code
                        ? 'border-uwr-orange text-uwr-orange bg-uwr-orange/10'
                        : 'border-white/15 text-white/70',
                    )}
                  >
                    <Flag code={c.countryCode} size="sm" className="rounded-sm" />
                    <span>
                      {c.symbol} {c.code}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Submenu panel */}
          <div
            className={cn(
              'absolute inset-0 overflow-y-auto px-6 py-6 transition-transform duration-300 ease-out',
              mobileSubmenu ? 'translate-x-0' : 'translate-x-full',
            )}
          >
            <button
              type="button"
              onClick={() => setMobileSubmenu(null)}
              className="flex items-center gap-2 mb-6 text-sm font-bold text-white/70 hover:text-white transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>{tCommon('back')}</span>
            </button>

            <h2 className="text-2xl font-extrabold uppercase tracking-wide text-white mb-6">
              {mobileSubmenu}
            </h2>

            <div className="flex flex-col">
              {(() => {
                const active = navItems.find((it) => it.label === mobileSubmenu);
                const items = active?.children ?? [];
                return items.map((child) => (
                  <Link
                    prefetch={false}
                    key={child.href}
                    href={buildLocalePath(locale, child.href)}
                    onClick={closeMobile}
                    className="group flex items-center gap-3 py-3 border-b border-white/10 hover:border-uwr-orange/40 transition-colors"
                  >
                    {child.icon && (
                      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center text-white/70 group-hover:bg-uwr-orange/15 group-hover:text-uwr-orange transition-colors">
                        {child.icon}
                      </div>
                    )}
                    <span className="flex-1 font-bold text-base text-white">{child.label}</span>
                  </Link>
                ));
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile account drawer — separate menu for the logged-in state.
          Slides in from the right so it's distinct from the main nav drawer. */}
      {user && (
        <div
          className={cn(
            'md:hidden fixed inset-0 z-[60] flex flex-col bg-stone-950 transition-all duration-300 ease-out',
            accountOpen
              ? 'opacity-100 translate-x-0'
              : 'opacity-0 translate-x-4 pointer-events-none',
          )}
        >
          {/* Top bar */}
          <div className="flex-shrink-0 h-20 flex items-center justify-between px-6 border-b border-white/10 border-t-2 border-t-uwr-orange">
            <p className="text-sm font-extrabold uppercase tracking-widest text-white/55">
              {tNav('myDashboard')}
            </p>
            <button
              type="button"
              onClick={closeAccount}
              className="text-white p-2 -mr-2"
              aria-label={tCommon('close')}
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-6">
            {/* User card */}
            <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
              <span className="flex-shrink-0 w-12 h-12 rounded-full overflow-hidden">
                {user.avatarUrl ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={user.avatarUrl}
                    alt={user.name}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <span className="w-full h-full flex items-center justify-center text-lg font-extrabold bg-uwr-orange text-white">
                    {user.name.charAt(0).toUpperCase()}
                  </span>
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-white truncate">{user.name}</p>
                {user.email && <p className="text-xs text-white/55 truncate">{user.email}</p>}
              </div>
            </div>

            {/* Admin workspace links (mirrors DGR PublicHeader) */}
            {workspaceLinks.length > 0 && (
              <nav className="mt-4 flex flex-col">
                {workspaceLinks.map((workspace) => {
                  const Icon = workspace.icon;
                  return (
                    <Link
                      key={workspace.href}
                      href={buildLocalePath(locale, workspace.href)}
                      prefetch={false}
                      onClick={closeAccount}
                      className="flex items-center gap-3 py-3 text-base font-extrabold text-uwr-orange border-b border-white/10 hover:text-uwr-orange-light transition-colors"
                    >
                      <Icon className="w-5 h-5" />
                      <span>{workspace.label}</span>
                    </Link>
                  );
                })}
              </nav>
            )}

            {/* Account links */}
            <nav className="mt-4 flex flex-col">
              {[
                { href: '/my/dashboard', icon: LayoutDashboard, label: tNav('myDashboard') },
                ...(entrantUsername
                  ? [
                      {
                        href: `/fundraiser/${entrantUsername}`,
                        icon: UserIcon,
                        label: tMy('viewPublicProfile'),
                      },
                    ]
                  : []),
                { href: '/my/events', icon: CalendarDays, label: tNav('myEvents') },
                { href: '/my/donations', icon: Heart, label: tNav('myDonations') },
                { href: '/my/downloads', icon: Download, label: tMy('navDownloads') },
                { href: '/my/offers', icon: Gift, label: tMy('navOffers') },
                { href: '/my/team/dashboard', icon: Users, label: tMy('navTeam') },
                { href: '/my/settings', icon: SettingsIcon, label: tMy('navSettings') },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.href}
                    href={buildLocalePath(locale, item.href)}
                    prefetch={false}
                    onClick={closeAccount}
                    className="flex items-center gap-3 py-3 text-base font-bold text-white border-b border-white/10 hover:text-uwr-orange transition-colors"
                  >
                    <Icon className="w-5 h-5 opacity-70" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
              <button
                type="button"
                onClick={() => {
                  closeAccount();
                  void logout();
                }}
                disabled={isLoggingOut}
                className="flex items-center gap-3 py-3 text-base font-bold text-white/80 hover:text-uwr-orange transition-colors"
              >
                <LogOut className="w-5 h-5 opacity-70" />
                <span>{isLoggingOut ? tNav('signingOut') : tNav('logout')}</span>
              </button>
            </nav>

            <Button
              asChild
              className="mt-6 bg-uwr-orange hover:bg-uwr-orange-hover text-white font-extrabold uppercase tracking-wider w-full h-11 rounded-full text-sm"
            >
              <Link
                prefetch={false}
                href={buildLocalePath(locale, '/donate')}
                onClick={closeAccount}
              >
                {tNav('donate')}
              </Link>
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
